/* eslint-disable max-lines */
import type { BundleProduct, SubscriptionProduct } from '../../core/models/module';
import { useEffect, useRef, useState } from 'react';
import { Observable } from 'tsbase/Patterns/Observable/Observable';
import { ITransaction, Transaction } from '../../core/domain/module';
import {
	Bundle,
	Giveaways,
	GiveawayWithEntriesList,
	IBundle,
	IGiveaways,
	ITracking,
	Tracking
} from '../../core/services/module';
import { Levels, PaymentFrequencies, ProductTiers, QueryParams } from '../../core/enums/module';
import { getGiveawayId } from '../../utilities/getGiveawayId';
import { getQueryParameter } from '../../utilities/getQueryParameter';
import { executeWithinBoundary } from '../../utilities/executeWithinBoundary';
import { cleanStringFromTerms } from '../../utilities/cleanStringFromTerms';
import { getSwiftWindBonus, SwiftWindConfig, swiftWindConfigurations } from '../../utilities/getSwiftWindConfig';
import { Icons } from '../../icons';
import { BundlesByTier, BundlesByTierKeys, BundlesByTierKeysLabelRecord, returnBundlesByTier } from './returnBundlesByTier';
import { getLevel } from '../../utilities/getLevel';
import { shouldUseCabelasBranding } from '../../utilities/shouldUseCabelasBranding';
import { ProductionCCMProductIds } from '../../core/enums/productionCcmProductIds';
import styles from './bonusesDrawer.module.scss';

type Props = {
	transaction?: ITransaction;
	bundleService?: IBundle;
	tracking?: ITracking;
	timeout?: number;
	getGiveawayIdFunc?: typeof getGiveawayId;
	giveaways?: IGiveaways;
	getQueryParamFunc?: typeof getQueryParameter;
	returnBundlesByTierFunc?: typeof returnBundlesByTier;
};

let autoCloseTimeout: number | null = null;
export const containerFocused = {
	value: false
};

export const BonusesDisplayed = new Observable<boolean>();
const quarterSecond = 250;

// eslint-disable-next-line complexity
export function BonusesDrawer({
	transaction = Transaction.Instance(),
	bundleService = Bundle.Instance(),
	tracking = Tracking.Instance(),
	timeout = quarterSecond,
	getGiveawayIdFunc = getGiveawayId,
	giveaways = Giveaways.Instance(),
	getQueryParamFunc = getQueryParameter,
	returnBundlesByTierFunc = returnBundlesByTier
}: Props) {
	const containerRef = useRef<HTMLDivElement>(null);
	const toggleButtonRef = useRef<HTMLButtonElement>(null);
	const [bundleProducts, setBundleProducts] = useState<BundleProduct[]>([]);
	const [sortedBundleProducts, setSortedBundleProducts] = useState<BundlesByTier | undefined>();
	const [purchaseProducts, setPurchaseProducts] = useState<
		| {
			primary?: SubscriptionProduct;
			spouse?: SubscriptionProduct;
		}
		| undefined
	>();
	const [giveaway, setGiveaway] = useState<GiveawayWithEntriesList>();
	const [swiftWindConfig, setSwiftWindConfig] = useState<SwiftWindConfig | undefined>();
	const [_userDeniedBonuses, setUserDeniedBonuses] = useState(false);

	const level = getLevel(transaction.Data.purchaseProducts?.primary?.attributes?.sku?.toLowerCase()) || Levels.Elite;
	const swiftWindBonusValues = swiftWindConfig && getSwiftWindBonus(swiftWindConfig, level);
	const lifeLockName = level === Levels.Elite ? 'Lifelock Ultimate Plus' : 'Lifelock Standard';
	const lifeLockValue = level === Levels.Elite ? '$339.99' : '$124.99';
	const primaryProduct = purchaseProducts?.primary;
	const isCcmPurchase = primaryProduct?.attributes?.tier === ProductTiers.CCM ||
		[primaryProduct?.id, primaryProduct?.attributes.externalId, primaryProduct?.attributes.externalServicesId]
			.some((id) => ProductionCCMProductIds.some((ccmId) => ccmId === id));
	const lifeLockIncluded = !!purchaseProducts?.primary && !isCcmPurchase;
	let changesSubId = '';
	let bonusesDisplayedSubId = '';
	const bonusCount = (!!giveaway ? giveaway.entries.length : 0) +
		(swiftWindBonusValues?.length ? (swiftWindBonusValues.length > 1 ? swiftWindBonusValues.length : swiftWindBonusValues[0].numberOfItems) : 0) +
		(bundleProducts.length || (
			(sortedBundleProducts?.eliteAndPlatinum.length || 0) + (sortedBundleProducts?.allTiers.length || 0)
		)) +
		(lifeLockIncluded ? 1 : 0);
	const cartItemCount = bonusCount + (purchaseProducts?.primary ? 1 : 0) + (purchaseProducts?.spouse ? 1 : 0);
	const fourPointSevenFiveSeconds = timeout * 19;
	const membershipUnselected = !(purchaseProducts?.primary || purchaseProducts?.spouse);
	const useCabelasBranding = shouldUseCabelasBranding();

	const updateDataFromTransaction = async () => {
		setUserDeniedBonuses(transaction.Data.bundleProductsDenied || false);
		setBundleProducts(transaction.Data.bundleProducts || []);
		setPurchaseProducts(transaction.Data.purchaseProducts);
		setGiveaway(transaction.Data.currentGiveaway);
		executeWithinBoundary(async () => {
			const productIdExists = !!getQueryParamFunc(QueryParams.ProductId);
			if (!productIdExists) {
				const allBundleProducts: Record<Levels, BundleProduct[]> | undefined =
					(await bundleService.getBundleProductsForAllLevels()).value || undefined;
				if (allBundleProducts) {
					const sortedBundles = returnBundlesByTierFunc(allBundleProducts);
					setSortedBundleProducts(sortedBundles);
				}
			}
		});
	};

	useEffect(() => {
		BonusesDisplayed.Publish(true);
	}, [bundleProducts, sortedBundleProducts]);

	useEffect(() => {
		changesSubId = transaction.Updates.Subscribe(updateDataFromTransaction);

		bonusesDisplayedSubId = BonusesDisplayed.Subscribe((b) => {
			if (b) {
				BonusesDisplayed.Cancel(bonusesDisplayedSubId);
				containerRef.current?.focus();

				autoCloseTimeout = setTimeout(() => {
					containerRef.current?.blur();
				}, fourPointSevenFiveSeconds) as any;
			}
		});

		executeWithinBoundary(async () => {
			const campaignId = await getGiveawayIdFunc();
			transaction.Data.currentGiveaway = campaignId ? (await giveaways.getGiveawayCampaign(campaignId)).value : undefined;

			const productId = getQueryParamFunc(QueryParams.ProductId);
			if (productId) {
				const productsResult = await transaction.SetProductDataById(productId);
				if (productsResult.wasSuccessful) {
					const isLegalProduct = !productsResult.value?.find(
						(p) => p.attributes.price === 0 || ['silver', 'lifetime'].some((t) => p.attributes.tier.toLowerCase().includes(t))
					);
					if (isLegalProduct) {
						const dataForProductId = transaction.SubscriptionProductForParam;
						const currentSubIsAnnual =
							transaction.Data.customerSubscriptions?.find((s) => s.attributes.status === 'active' && s.attributes.paymentFrequency)
								?.attributes.paymentFrequency === PaymentFrequencies.Annual;
						const productIdIsAnnual = dataForProductId?.attributes.paymentFrequency === PaymentFrequencies.Annual;
						const frequency: PaymentFrequencies =
							currentSubIsAnnual || productIdIsAnnual ? PaymentFrequencies.Annual : PaymentFrequencies.Monthly;
						transaction.SetPurchaseProducts(frequency as PaymentFrequencies);
						await bundleService.setBundleProductsInTransaction();
					}
				}
			}

			await updateDataFromTransaction();
			const funnelIdResult = await tracking.getFunnelId();
			if (funnelIdResult.wasSuccessful && funnelIdResult.value) {
				setSwiftWindConfig(swiftWindConfigurations[funnelIdResult.value]);
			}
		});

		return () => {
			transaction.Updates.Cancel(changesSubId);
			BonusesDisplayed.Cancel(bonusesDisplayedSubId);
		};
	}, []);

	const ToggleButtonText = () => {
		const hasExactlyOneBonus = bonusCount === 1;
		const hasLevelSpecificBonuses =
			sortedBundleProducts &&
			Object.values(BundlesByTierKeys).some((tier) => tier != BundlesByTierKeys.AllTiers && sortedBundleProducts[tier]?.length);
		if (!bonusCount) {
			return <span>View Cart</span>;
		}
		return membershipUnselected ? (
			<span>
				Get{hasLevelSpecificBonuses ? ' up to' : ''} {bonusCount} free {hasExactlyOneBonus ? 'bonus' : 'bonuses'}
			</span>
		) : (
			<span>
				{bonusCount} free {hasExactlyOneBonus ? 'bonus' : 'bonuses'} in cart
			</span>
		);
	};

	return !useCabelasBranding ? (
		<div
			tabIndex={0}
			ref={containerRef}
			onFocus={() => setTimeout(() => (containerFocused.value = true), timeout)}
			onBlur={() => setTimeout(() => (containerFocused.value = false), timeout)}
			className={`${styles.container} ${styles.narrow}`}
		>
			<button
				ref={toggleButtonRef}
				id="bonusesDrawer_toggle_button"
				onClick={() => {
					autoCloseTimeout && clearTimeout(autoCloseTimeout);
					if (containerFocused.value) {
						containerRef.current?.blur();
						toggleButtonRef.current?.blur();
						containerFocused.value = false;
					}
				}}
			>
				<div className={`${styles.cartTextChevronWrapper}`}>
					<ToggleButtonText />
					<div className={styles.togglableIcon}>
						<div className={styles.down}>{Icons.ChevronDown}</div>
						<div className={styles.up}>{Icons.ChevronUp}</div>
					</div>
				</div>

				<div className={styles.cartIconWrapper}>
					{Icons.ShoppingCart}{' '}
					{<span className={`${membershipUnselected && styles.hidden}`}>{!membershipUnselected && cartItemCount}</span>}
				</div>
			</button>

			<ul>
				{(
					<>
						{!!bundleProducts.length ? (
							<>
								{bundleProducts.map((product, i) => (
									<li key={i}>
										<img src={product?.attributes?.image || ''} alt={product?.attributes?.description || ''} />
										<p>
											<strong>Free</strong> {product?.attributes?.description}
											<br />
											{!!product?.attributes?.listPrice && <em>Valued at ${product.attributes.listPrice}</em>}
										</p>
									</li>
								))}
								{!!swiftWindBonusValues && swiftWindBonusValues.map(e => <li>
									<img src={e.imgSrc} alt={e.description} />
									<p>
										<strong>{e.numberOfItems > 1 ? `${e.numberOfItems} Free` : 'Free'}</strong>
										{` ${e.description}`}
										<br />
										{!!e.monetaryValue && <em>Valued at ${e.monetaryValue}</em>}
									</p>
								</li>)}
							</>
						) : (
							<>
								{Object.values(BundlesByTierKeys).map((tier) => (
									<>
										{!!sortedBundleProducts?.[tier].length &&
											sortedBundleProducts[tier].map((product, i) => (
												<li key={i}>
													<img src={product?.attributes?.image || ''} alt={product?.attributes?.description || ''} />
													<p>
														<strong>{BundlesByTierKeysLabelRecord[tier]}</strong>
														<br />
														<strong>Free</strong> {product?.attributes?.description} <em>(${product?.attributes?.listPrice})</em>
													</p>
												</li>
											))}
									</>
								))}
								{!!swiftWindBonusValues && swiftWindBonusValues.map(e => <li>
									<img src={e.imgSrc} alt={e.description} />
									<p>
										<strong>{BundlesByTierKeysLabelRecord[e.tier]}</strong>
										<br />
										<strong>{e.numberOfItems > 1 ? `${e.numberOfItems} Free` : 'Free'}</strong>
										{` ${e.description} `}
										{e.monetaryValue > 1 && <em>(${e.monetaryValue})</em>}
									</p>
								</li>)}
							</>
						)}
						{giveaway?.entries?.map((g, i) => (
							<li key={i}>
								<img src={g.image} alt="Entry to win" />
								<p>
									<strong>Free Entry to Win</strong>
									<br />
									{g.description}
								</p>
							</li>
						))}
					</>
				)}
				{purchaseProducts?.primary && (
					<li>
						<img src={purchaseProducts.primary.attributes.image} alt="" />
						<p>
							<b>{cleanStringFromTerms(purchaseProducts.primary.attributes.description, ['Monthly', 'Annual', 'USCCA', '15.0', 'Loyalty'])}</b>
							<br />
							Billed {purchaseProducts.primary.attributes.paymentFrequency === PaymentFrequencies.Monthly ? 'Monthly' : 'Annually'}
						</p>
					</li>
				)}
				{lifeLockIncluded && (
					<li>
						<img src="https://offers.deltadefense.com/images/lifelock.avif" alt="Lifelock" />
						<p>
							<strong>{lifeLockName}</strong>
							<br />
							<em>Valued at {lifeLockValue}</em>
						</p>
					</li>
				)}
				{purchaseProducts?.spouse && (
					<li>
						<img src={purchaseProducts.spouse.attributes.image} alt="" />
						<p>
							<b>{cleanStringFromTerms(purchaseProducts.spouse.attributes.description, ['Monthly', 'Annual', 'USCCA', '15.0', 'Loyalty'])}</b>
							<br />
							Billed {purchaseProducts.spouse.attributes.paymentFrequency === PaymentFrequencies.Monthly ? 'Monthly' : 'Annually'}
						</p>
					</li>
				)}
				{membershipUnselected && (
					<li>
						{Icons.MembershipShield}
						<p>
							<b>
								USCCA Membership{!!bonusCount && <><br />Plus Free Bonus(es)</>}
							</b>
						</p>
					</li>
				)}
			</ul>
		</div>
	) : <></>;
}
